import { failSilently } from '../../utils/failSilently';
import { sendGA4Event } from '../shared/sendGa4Event';
import type {
    ClickRecipeEvent,
    FavoriteRecipeEvent,
    FavoriteRecipeInteractionType,
    FilterRecipesEvent,
    PdpRecipeRecommendationsClickEvent,
    RateRecipeEvent,
    RecipeAdClickEvent,
    RecipeKeepScreenOnEvent,
    RecipeKeepScreenOnStatus,
    RecipeRecommendationsClickType,
    RecipeRecommendationsEventType,
    ReviewRecipeEvent,
    WeeklyMenuCategoryPillClickEvent,
    WeeklyMenuFaqClickEvent,
    WeeklyMenuRecipeHandleIngredientEvent,
    WeeklyMenuRecipeHandleIngredientType,
    WeeklyMenuRecipeInteractionEvent,
    WeeklyMenuRecipeInteractionType,
    WeeklyMenuTeaserArrowClickEvent,
} from './ga4Recipes';

const filterRecipes = (filter: string) => {
    const event: FilterRecipesEvent = {
        event: 'filter',
        filter_category_1: filter.toLowerCase(),
        event_key: 'filter_recipes',
    };

    sendGA4Event(event);
};

const favoriteRecipe =
    (type: FavoriteRecipeInteractionType) => (recipeId: string, recipeName: string) => {
        const event: FavoriteRecipeEvent = {
            event: 'favourite',
            recipe_id: recipeId,
            recipe_name: recipeName.toLowerCase(),
            feature_name: 'recipe',
            type,
            event_key: `favourite_recipe_${type}`,
        };

        sendGA4Event(event);
    };

const rateRecipe = (recipeId: number, recipeName: string, rating: number) => {
    const event: RateRecipeEvent = {
        event: 'rate',
        recipe_id: recipeId.toString(),
        rating,
        recipe_name: recipeName.toLowerCase(),
        event_key: 'rate_recipe',
    };

    sendGA4Event(event);
};

const recipeAdClick = (
    promoId: string,
    promoName: string,
    creativeName: string,
    creativeSlot: string,
) => {
    const event: RecipeAdClickEvent = {
        event: 'select_promotion',
        ecommerce: {
            promotion_id: promoId,
            promotion_name: promoName,
            creative_name: creativeName,
            creative_slot: creativeSlot,
        },
    };

    sendGA4Event(event);
};

const reviewRecipe = (recipeId: number, recipeName: string) => {
    const event: ReviewRecipeEvent = {
        event: 'review',
        recipe_id: recipeId.toString(),
        recipe_name: recipeName.toLowerCase(),
        event_key: 'review_recipe',
    };

    sendGA4Event(event);
};

const weeklyMenuRecipeInteraction =
    (type: WeeklyMenuRecipeInteractionType) =>
    (recipeType: string, recipeName: string, menuConfiguration: 'custom' | 'default') => {
        const event: WeeklyMenuRecipeInteractionEvent = {
            event: `weekly_menu_recipe`,
            type,
            recipe_name: recipeName.toLowerCase(),
            recipe_type: recipeType.toLowerCase(),
            menu_configuration: menuConfiguration,
        };

        sendGA4Event(event);
    };

const weeklyMenuRecipeHandleIngredient =
    (type: WeeklyMenuRecipeHandleIngredientType) =>
    (recipeExternalId: string, recipeName: string, productId: string, productName: string) => {
        const event: WeeklyMenuRecipeHandleIngredientEvent = {
            event: 'weekly_menu_handle_ingredients',
            event_action: type,
            item_name: productName,
            item_id: productId,
            recipe_id: recipeExternalId,
            recipe_name: recipeName,
        };

        sendGA4Event(event);
    };

const weeklyMenuTeaserArrowClick = (recipeType: string, cardNumber: number) => {
    const event: WeeklyMenuTeaserArrowClickEvent = {
        event: `weekly_menu_swiper_arrow_click`,
        card: cardNumber.toString(),
        recipe_type: recipeType,
    };

    sendGA4Event(event);
};

const weeklyMenuCategoryPillClick = (recipeType: string) => {
    const event: WeeklyMenuCategoryPillClickEvent = {
        event: `weekly_menu_recipe_type_click`,
        recipe_type: recipeType,
    };

    sendGA4Event(event);
};

const recipeRecommendationsClick =
    (eventType: RecipeRecommendationsEventType) =>
    (type: RecipeRecommendationsClickType) =>
    (recipeId: number, recipeName: string) => {
        const event: PdpRecipeRecommendationsClickEvent = {
            event: eventType,
            type,
            recipe_id: recipeId.toString(),
            recipe_name: recipeName.toLowerCase(),
        };

        sendGA4Event(event);
    };

const faqClick = (text: string, action: WeeklyMenuFaqClickEvent['event_action']) => {
    const event: WeeklyMenuFaqClickEvent = {
        event: 'faq_interaction',
        click_text: text,
        event_action: action,
        type: 'weekly menu',
    };

    sendGA4Event(event);
};

const click = (type: ClickRecipeEvent['type']) => (id: string, name: string) => {
    const event: ClickRecipeEvent = {
        event: 'recipe_click',
        recipe_id: id,
        recipe_name: name,
        type,
    };

    sendGA4Event(event);
};
const recipeKeepScreenOnClick = (status: RecipeKeepScreenOnStatus) => () => {
    const event: RecipeKeepScreenOnEvent = {
        event: `keep_screen_on`,
        status,
    };

    sendGA4Event(event);
};

export default {
    click: {
        recipe: failSilently(click('standard recipe')),
        buyableRecipe: failSilently(click('buyable recipe')),
        foodbox: failSilently(click('standard foodbox')),
        buyableFoodbox: failSilently(click('buyable foodbox')),
        weeklyMenu: failSilently(click('weekly menu')),
    },
    filterRecipes: failSilently(filterRecipes),
    rateRecipe: failSilently(rateRecipe),
    recipeAdClick: failSilently(recipeAdClick),
    reviewRecipe: failSilently(reviewRecipe),
    recipeKeepScreenOnClick: {
        activate: failSilently(recipeKeepScreenOnClick('activate')),
        deactivate: failSilently(recipeKeepScreenOnClick('deactivate')),
    },
    favoriteRecipe: {
        add: failSilently(favoriteRecipe('add')),
        remove: failSilently(favoriteRecipe('remove')),
    },
    weeklyMenu: {
        addRecipe: failSilently(weeklyMenuRecipeInteraction('add')),
        removeRecipe: failSilently(weeklyMenuRecipeInteraction('remove')),
        arrowClick: failSilently(weeklyMenuTeaserArrowClick),
        categoryPillClick: failSilently(weeklyMenuCategoryPillClick),
        handleIngredient: failSilently(weeklyMenuRecipeHandleIngredient),
        faqClick: failSilently(faqClick),
    },
    pdpRecipeRecommendations: {
        buyButtonClick: failSilently(
            recipeRecommendationsClick('pdp_recommended_recipes')('button_recipe_click'),
        ),
        recipeTeaserClick: failSilently(
            recipeRecommendationsClick('pdp_recommended_recipes')('product_card_click'),
        ),
    },
    plpRecipeRecommendations: {
        buyButtonClick: failSilently(
            recipeRecommendationsClick('plp_recommended_recipes')('button_recipe_click'),
        ),
        recipeTeaserClick: failSilently(
            recipeRecommendationsClick('plp_recommended_recipes')('product_card_click'),
        ),
    },
};
